/**
 * Client-side mirror of the backend detection catalog for read-only display.
 * Grouped by tier for the Built-in Catalog Panel.
 *
 * Kept in sync with apps/backend/src/policies/extension-security/catalog.ts
 * (drift is guarded by catalog.test.ts in the backend workspace). Output is
 * decided by the aggregate weighted score, not per-row severity (KIR-2691);
 * only the weight-100 credential-targeting rules are findings on their own.
 */

export interface CatalogRuleDisplay {
  id: string;
  field: string;
  matchType: string;
  description: string;
  severity: 'info' | 'low' | 'medium' | 'high' | 'critical';
  /** Weighted-scoring weight (0 = never contributes, 100 = instant finding). */
  weight: number;
  /** Correlation group — only the highest weight in a group counts. */
  correlationGroup?: string;
}

/**
 * Weight at which a single rule is an instant finding on its own (mirrors the
 * backend `INSTANT_SIGNAL_WEIGHT`). Everything below it is capability metadata
 * that only contributes to the aggregate weighted score.
 */
export const INSTANT_SIGNAL_WEIGHT = 100;

/**
 * Mirrors backend `FINDING_SCORE_THRESHOLD` / `HIGH_SCORE_THRESHOLD` in
 * `apps/backend/src/policies/extension-security/scoring.ts` (same drift pattern
 * as catalog weights — KIR-3781 Why-this-fired copy).
 */
export const FINDING_SCORE_THRESHOLD = 50;
export const HIGH_SCORE_THRESHOLD = 80;

/**
 * Derive a rule's catalog output from its weight rather than storing it per
 * row: `output` is exactly `weight >= INSTANT_SIGNAL_WEIGHT`, so duplicating it
 * on every row would let the two drift silently (the backend↔frontend drift
 * test does not validate `output`).
 */
export function catalogRuleOutput(
  rule: Pick<CatalogRuleDisplay, 'weight'>
): 'finding' | 'capability' {
  return rule.weight >= INSTANT_SIGNAL_WEIGHT ? 'finding' : 'capability';
}

export interface CatalogTier {
  name: string;
  label: string;
  rules: CatalogRuleDisplay[];
}

export const CATALOG_TIERS: CatalogTier[] = [
  {
    name: 'ai-ide',
    label: 'AI-IDE Specific',
    rules: [
      {
        id: 'catalog_mcp_server_provider',
        field: 'contributes.mcpServerDefinitionProviders',
        matchType: 'exists',
        description:
          'Can register MCP servers for AI assistants. Common in AI extensions.',
        severity: 'info',
        weight: 0,
      },
      {
        id: 'catalog_language_model_tools',
        field: 'contributes.languageModelTools',
        matchType: 'exists',
        description: 'Tools an in-IDE LLM can call. Visibility signal only.',
        severity: 'info',
        weight: 0,
      },
      {
        id: 'catalog_chat_participant',
        field: 'contributes.chatParticipants',
        matchType: 'exists',
        description: 'Joins the AI chat interface. Very common.',
        severity: 'info',
        weight: 0,
      },
      {
        id: 'catalog_api_proposal_mcp',
        field: 'enabledApiProposals',
        matchType: 'arrayContains',
        description:
          'Enables the MCP server definitions API proposal. Mirrors contributes.mcpServerDefinitionProviders.',
        severity: 'info',
        weight: 0,
      },
      {
        id: 'catalog_api_proposal_lm_tools',
        field: 'enabledApiProposals',
        matchType: 'arrayContains',
        description:
          'Enables the language model tools API proposal. Mirrors contributes.languageModelTools.',
        severity: 'info',
        weight: 0,
      },
      {
        id: 'catalog_api_proposal_chat',
        field: 'enabledApiProposals',
        matchType: 'arrayContains',
        description:
          'Enables the chat participants API proposal. Mirrors contributes.chatParticipants.',
        severity: 'info',
        weight: 0,
      },
      {
        id: 'catalog_ai_activation',
        field: 'activationEvents',
        matchType: 'arrayContains',
        description: 'Activates when an LLM tool is invoked.',
        severity: 'info',
        weight: 0,
      },
      {
        id: 'catalog_chat_activation',
        field: 'activationEvents',
        matchType: 'arrayContains',
        description: 'Activates when an AI chat participant is triggered.',
        severity: 'info',
        weight: 0,
      },
    ],
  },
  {
    name: 'capability-registration',
    label: 'Capability Registration',
    rules: [
      {
        id: 'catalog_credential_provider',
        field: 'contributes.authentication',
        matchType: 'exists',
        description:
          'Registers an auth provider. Can ask for credentials or tokens.',
        severity: 'medium',
        weight: 38,
        correlationGroup: 'auth-credential',
      },
      {
        id: 'catalog_debugger',
        field: 'contributes.debuggers',
        matchType: 'exists',
        description: 'Registers a debug adapter, can run processes.',
        severity: 'medium',
        weight: 38,
      },
      {
        id: 'catalog_typescript_server_plugin',
        field: 'contributes.typescriptServerPlugins',
        matchType: 'exists',
        description:
          'Rare. Runs inside the TS server with the same trust as TypeScript.',
        severity: 'medium',
        weight: 42,
      },
      {
        id: 'catalog_custom_editor',
        field: 'contributes.customEditors',
        matchType: 'exists',
        description: 'Replaces the editor, can intercept reads and writes.',
        severity: 'medium',
        weight: 32,
      },
      {
        id: 'catalog_terminal',
        field: 'contributes.terminal',
        matchType: 'exists',
        description: 'Terminal profile that can run shell commands.',
        severity: 'high',
        weight: 48,
        correlationGroup: 'shell-execution',
      },
      {
        id: 'catalog_tasks',
        field: 'contributes.tasks',
        matchType: 'exists',
        description: 'Tasks that can run shell commands or scripts.',
        severity: 'high',
        weight: 48,
        correlationGroup: 'shell-execution',
      },
      {
        id: 'catalog_filesystem_providers',
        field: 'contributes.fileSystemProviders',
        matchType: 'exists',
        description: 'Virtual filesystem, can intercept file reads and writes.',
        severity: 'high',
        weight: 49,
        correlationGroup: 'fs-access',
      },
      {
        id: 'catalog_notebook_renderer',
        field: 'contributes.notebookRenderer',
        matchType: 'exists',
        description: 'Custom notebook output renderer. Common in notebooks.',
        severity: 'low',
        weight: 12,
      },
    ],
  },
  {
    name: 'activation-patterns',
    label: 'Activation Patterns',
    rules: [
      {
        id: 'catalog_credential_env',
        field: 'activationEvents',
        matchType: 'arrayContains',
        description:
          'Credential file targeting (.env). Instant. Fires even for trusted publishers.',
        severity: 'high',
        weight: 100,
      },
      {
        id: 'catalog_credential_aws',
        field: 'activationEvents',
        matchType: 'arrayContains',
        description:
          'Credential file targeting (.aws/credentials). Instant. Fires even for trusted publishers.',
        severity: 'high',
        weight: 100,
      },
      {
        id: 'catalog_credential_ssh',
        field: 'activationEvents',
        matchType: 'arrayContains',
        description:
          'Credential file targeting (.ssh/id_rsa). Instant. Fires even for trusted publishers.',
        severity: 'high',
        weight: 100,
      },
      {
        id: 'catalog_activation_auth_request',
        field: 'activationEvents',
        matchType: 'arrayContains',
        description: 'Wakes up during sensitive auth flows.',
        severity: 'high',
        weight: 47,
        correlationGroup: 'auth-credential',
      },
      {
        id: 'catalog_activation_filesystem',
        field: 'activationEvents',
        matchType: 'arrayContains',
        description: 'Filesystem activation.',
        severity: 'high',
        weight: 49,
        correlationGroup: 'fs-access',
      },
      {
        id: 'catalog_activation_terminal',
        field: 'activationEvents',
        matchType: 'arrayContains',
        description: 'Terminal activation.',
        severity: 'high',
        weight: 48,
        correlationGroup: 'shell-execution',
      },
      {
        id: 'catalog_activation_uri',
        field: 'activationEvents',
        matchType: 'arrayContains',
        description: 'Deep-link activation, can be triggered by a browser.',
        severity: 'medium',
        weight: 28,
      },
      {
        id: 'catalog_activation_wildcard',
        field: 'activationEvents',
        matchType: 'arrayContains',
        description: 'Wakes up as soon as the editor starts.',
        severity: 'low',
        weight: 25,
        correlationGroup: 'activation-broad',
      },
      {
        id: 'catalog_activation_startup_finished',
        field: 'activationEvents',
        matchType: 'arrayContains',
        description: 'Wakes up after startup.',
        severity: 'low',
        weight: 12,
        correlationGroup: 'activation-broad',
      },
    ],
  },
  {
    name: 'supply-chain',
    label: 'Supply Chain / Trust Posture',
    rules: [
      {
        id: 'catalog_extension_dependencies',
        field: 'extensionDependencies',
        matchType: 'exists',
        description:
          'Force-installs other extensions. Common, weak on its own.',
        severity: 'info',
        weight: 8,
      },
      {
        id: 'catalog_extension_pack',
        field: 'extensionPack',
        matchType: 'exists',
        description:
          'Force-installs other extensions. Common, weak on its own.',
        severity: 'info',
        weight: 8,
      },
      {
        id: 'catalog_postinstall_script',
        field: 'scripts.postinstall',
        matchType: 'exists',
        description: 'npm install script. Needs code evidence to act on.',
        severity: 'info',
        weight: 30,
        correlationGroup: 'install-scripts',
      },
      {
        id: 'catalog_preinstall_script',
        field: 'scripts.preinstall',
        matchType: 'exists',
        description: 'npm install script. Needs code evidence to act on.',
        severity: 'info',
        weight: 30,
        correlationGroup: 'install-scripts',
      },
      {
        id: 'catalog_untrusted_workspace_support',
        field: 'capabilities.untrustedWorkspaces.supported',
        matchType: 'exact',
        description: 'Self-claim of safety in untrusted workspaces.',
        severity: 'low',
        weight: 8,
      },
      {
        id: 'catalog_no_repository',
        field: 'repository',
        matchType: 'notExists',
        description: 'No repository link. Transparency signal.',
        severity: 'info',
        weight: 0,
      },
      {
        id: 'catalog_sponsor_funding',
        field: 'sponsor',
        matchType: 'exists',
        description: 'Sponsor or funding link present. Transparency signal.',
        severity: 'info',
        weight: 0,
      },
      {
        id: 'catalog_web_extension',
        field: 'browser',
        matchType: 'exists',
        description: 'Declares a browser entry point for web extensions.',
        severity: 'info',
        weight: 0,
      },
    ],
  },
];
